import React from "react";
import { Link } from "react-router-dom";
import { getAccessToken } from "../../utils/token";
import apiClient from "../../services/apiClient";
import reportService from "../../services/reportService";
import { API_CONFIG } from "../../utils/apiConfig";
import {
  getReportTheme,
  loadLogoImage,
  downloadBlob,
  generateSummaryPDFFromData,
  generateSummaryExcelHtmlFromData,
  generatePDFFromData,
  generateExcelHtmlFromData
} from "../../utils/stockReportExport";

// Dynamic environment file URL normalizer
const normalizeFileUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  const currentApiBase = (API_CONFIG?.baseUrl || "").replace(/\/+$/, "");

  // 1. If relative URL, prepend current environment API base
  if (trimmed.startsWith("/")) {
    return `${currentApiBase}${trimmed}`;
  }

  // 2. If absolute URL, check if it points to an /api/ or /uploads/ endpoint
  try {
    const parsed = new URL(trimmed);
    if (parsed.pathname.includes("/api/") || parsed.pathname.includes("/uploads/")) {
      const currentHost = currentApiBase ? new URL(currentApiBase).host : "";
      if (currentHost && parsed.host !== currentHost) {
        return `${currentApiBase}${parsed.pathname}${parsed.search}`;
      }
    }
  } catch (_) {}

  return trimmed;
};

export default function ChatCardRenderer({ content, onNavigate }) {
  const [downloadingMap, setDownloadingMap] = React.useState({});

  if (!content || !content.type || !content.data) return null;

  const { type, data } = content;

  // Rich In-Chat File Download Card (Supports single or multiple files: PDF + Excel + CSV)

  if (type === "file_download" && data) {
    const rawFileList = Array.isArray(data.files) && data.files.length > 0
      ? data.files
      : (data.fileUrl ? [{
        format: data.fileType || "pdf",
        fileName: data.fileName || "document.pdf",
        fileUrl: data.fileUrl,
        fileType: data.fileType || "pdf",
        label: data.fileType === "excel" || data.fileType === "xlsx" ? "Excel Report" : "PDF Document"
      }] : []);

    const fileList = rawFileList.map((f) => ({
      ...f,
      fileUrl: normalizeFileUrl(f.fileUrl)
    }));

    const handleDownload = async (e, rawFileUrl, fileName, format) => {
      e.preventDefault();
      const fileUrl = normalizeFileUrl(rawFileUrl);
      if (!fileUrl) return;

      const isXlsx = format === "xlsx" || format === "excel" || (fileName && /\.(xlsx|xls)$/i.test(fileName));
      setDownloadingMap((prev) => ({ ...prev, [fileUrl]: true }));

      try {
        const isStockSummary = fileUrl.includes("stock-summary-report") || data.documentType === "stock_summary_report" || (data.title && /summary/i.test(data.title));
        const isDetailedStock = !isStockSummary && (fileUrl.includes("stock-report") || data.documentType === "stock_report" || (data.title && /stock/i.test(data.title)));
        const isStockReport = isStockSummary || isDetailedStock;

        // If this is a stock report, use the official ERP report generators
        if (isStockReport) {
          let rows = (Array.isArray(data.categories) && data.categories.length > 0)
            ? data.categories
            : (Array.isArray(data.raw?.categories) && data.raw.categories.length > 0 ? data.raw.categories : null);

          if (!rows || rows.length === 0) {
            try {
              if (isStockSummary) {
                const res = await reportService.getStockSummaryReport();
                rows = res?.categories || res?.items || res || [];
              } else {
                const res = await reportService.getStockReport();
                rows = res?.categories || res?.items || res || [];
              }
            } catch (loadErr) {
              console.warn("Failed fetching stock rows for export, falling back to endpoint:", loadErr);
            }
          }

          if (rows && rows.length > 0) {
            if (isXlsx) {
              const html = isStockSummary
                ? generateSummaryExcelHtmlFromData(rows)
                : generateExcelHtmlFromData(rows);
              const xlsBlob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
              const exportName = fileName ? fileName.replace(/\.xlsx$/i, ".xls") : (isStockSummary ? "stock-summary-report-all.xls" : "stock-report-all.xls");
              downloadBlob(xlsBlob, exportName);
              setDownloadingMap((prev) => ({ ...prev, [fileUrl]: false }));
              return;
            } else {
              const theme = getReportTheme();
              const logoImg = await loadLogoImage(theme.logoSrc);
              const doc = isStockSummary
                ? generateSummaryPDFFromData(rows, logoImg, theme)
                : generatePDFFromData(rows, logoImg, theme);
              const exportName = fileName || (isStockSummary ? "stock-summary-report-all.pdf" : "stock-report-all.pdf");
              doc.save(exportName);
              setDownloadingMap((prev) => ({ ...prev, [fileUrl]: false }));
              return;
            }
          }
        }

        // Standard ERP file / attachment download via apiClient
        let blob = null;
        try {
          const clientRes = await apiClient.get(fileUrl, { responseType: "blob" });
          if (clientRes && clientRes.data) {
            blob = clientRes.data;
          }
        } catch (apiErr) {
          console.warn("apiClient download attempt failed, falling back to fetch:", apiErr);
        }

        if (!blob) {
          const token = getAccessToken() || localStorage.getItem("ims_auth_access_token") || localStorage.getItem("token");
          const res = await fetch(fileUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (res.ok) {
            blob = await res.blob();
          }
        }

        if (blob) {
          const mimeType = isXlsx
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf";
          const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = fileName || (isXlsx ? "report.xlsx" : "document.pdf");
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
          setDownloadingMap((prev) => ({ ...prev, [fileUrl]: false }));
          return;
        }
      } catch (err) {
        console.warn("Download failed, falling back to window.open:", err);
      } finally {
        setDownloadingMap((prev) => ({ ...prev, [fileUrl]: false }));
      }

      window.open(fileUrl, "_blank");
    };

    const handlePreview = async (e, rawFileUrl) => {
      e.preventDefault();
      const fileUrl = normalizeFileUrl(rawFileUrl);
      if (!fileUrl) return;

      try {
        const isStockSummary = fileUrl.includes("stock-summary-report") || data.documentType === "stock_summary_report" || (data.title && /summary/i.test(data.title));
        const isDetailedStock = !isStockSummary && (fileUrl.includes("stock-report") || data.documentType === "stock_report" || (data.title && /stock/i.test(data.title)));
        const isStockReport = isStockSummary || isDetailedStock;

        if (isStockReport) {
          let rows = (Array.isArray(data.categories) && data.categories.length > 0)
            ? data.categories
            : (Array.isArray(data.raw?.categories) && data.raw.categories.length > 0 ? data.raw.categories : null);

          if (!rows || rows.length === 0) {
            try {
              if (isStockSummary) {
                const res = await reportService.getStockSummaryReport();
                rows = res?.categories || res?.items || res || [];
              } else {
                const res = await reportService.getStockReport();
                rows = res?.categories || res?.items || res || [];
              }
            } catch (_) {}
          }

          if (rows && rows.length > 0) {
            const theme = getReportTheme();
            const logoImg = await loadLogoImage(theme.logoSrc);
            const doc = isStockSummary
              ? generateSummaryPDFFromData(rows, logoImg, theme)
              : generatePDFFromData(rows, logoImg, theme);
            const blobUrl = doc.output("bloburl");
            window.open(blobUrl, "_blank");
            return;
          }
        }

        let blob = null;
        try {
          const clientRes = await apiClient.get(fileUrl, { responseType: "blob" });
          if (clientRes && clientRes.data) {
            blob = clientRes.data;
          }
        } catch (_) {}

        if (!blob) {
          const token = getAccessToken() || localStorage.getItem("ims_auth_access_token") || localStorage.getItem("token");
          const res = await fetch(fileUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (res.ok) {
            blob = await res.blob();
          }
        }

        if (blob) {
          const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
          window.open(blobUrl, "_blank");
          return;
        }
      } catch (_) {}
      window.open(fileUrl, "_blank");
    };

    return (
      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition hover:shadow-md">
        {/* Card Header */}
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate max-w-[240px]">
              {data.title || "ERP Reports Ready"}
            </span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/60">
            {fileList.length > 1 ? `${fileList.length} Formats Available` : "Verified ERP File"}
          </span>
        </div>

        {/* Card Subtitle */}
        {data.subtitle && (
          <div className="px-3.5 pt-2 pb-1 text-[11px] text-slate-500 dark:text-slate-400 border-b border-slate-100/60 dark:border-slate-800/40 bg-slate-50/30 dark:bg-slate-900/30">
            {data.subtitle}
          </div>
        )}

        {/* Multi-Format File List (Renders both PDF and Excel) */}
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {fileList.map((file, fIdx) => {
            const isPdf = file.fileType === "pdf" || file.format === "pdf" || (file.fileName && file.fileName.toLowerCase().endsWith(".pdf"));
            const isExcel = file.fileType === "excel" || file.fileType === "xlsx" || file.format === "excel" || file.format === "xlsx" || (file.fileName && /\.(xlsx|xls)$/i.test(file.fileName));
            const isCsv = file.fileType === "csv" || file.format === "csv";
            const isDownloading = Boolean(downloadingMap[file.fileUrl]);

            return (
              <div key={fIdx} className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-xl shrink-0 font-black text-[10px] shadow-2xs border ${isPdf
                      ? "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200 dark:border-rose-900/60"
                      : isExcel
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60"
                        : isCsv
                          ? "bg-sky-50 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300 border-sky-200 dark:border-sky-900/60"
                          : "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-900/60"
                      }`}
                  >
                    {isPdf ? "PDF" : isExcel ? "XLS" : isCsv ? "CSV" : "DOC"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h5 className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate" title={file.fileName}>
                      {file.fileName}
                    </h5>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                      {isPdf ? "Official PDF Document" : isExcel ? "Excel Spreadsheet (Live Data)" : "Data Export"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                  {isPdf && file.fileUrl && (
                    <button
                      type="button"
                      onClick={(e) => handlePreview(e, file.fileUrl)}
                      title="Preview PDF in new tab"
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[11px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition cursor-pointer shadow-2xs"
                    >
                      <svg className="w-3 h-3 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>Preview</span>
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={(e) => handleDownload(e, file.fileUrl, file.fileName, file.format)}
                    title={`Download ${file.fileName}`}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white shadow-xs transition active:scale-95 cursor-pointer ${isDownloading ? "opacity-75 cursor-wait " : ""}${isExcel
                      ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20"
                      : "bg-primary-600 hover:bg-primary-700 shadow-primary-600/20"
                      }`}
                  >
                    {isDownloading ? (
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                    <span>{isDownloading ? "Downloading..." : `Download ${isPdf ? "PDF" : isExcel ? "Excel" : ""}`}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "stock_summary" && data.categories) {
    const list = data.categories.slice(0, 6);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📊 Live Stock Ledger ({data.totalCategories || list.length} Grades)
          </span>
          <Link
            to="/reports/stock-report"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            View Full Report →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">
              <tr>
                <th className="px-3 py-1.5 font-medium">Category</th>
                <th className="px-2 py-1.5 font-medium text-right">Available (kg)</th>
                <th className="px-3 py-1.5 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.map((cat, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                  <td className="px-3 py-1.5 font-medium text-slate-900 dark:text-slate-100 truncate max-w-[120px]">
                    {cat.categoryName}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800 dark:text-slate-200">
                    {Number(cat.availableStockKg || 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <span
                      className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium ${cat.status === "Low Stock Warning"
                        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                        : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                        }`}
                    >
                      {cat.status === "Low Stock Warning" ? "Low" : "Optimal"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.totalInventoryTonnageKg !== undefined && (
          <div className="border-t border-slate-100 bg-slate-50/50 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-300">
            Total Inventory:{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {Number(data.totalInventoryTonnageKg).toLocaleString("en-IN", {
                maximumFractionDigits: 1,
              })}{" "}
              kg
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── Lot Comprehensive Report Card ──────────────────────────────────────────
  if (type === "lot_comprehensive" && data.lots) {
    const list = data.lots.slice(0, 4);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📦 Lot Segregation Details ({data.lots.length} Lots)
          </span>
          <Link
            to="/reports/lot-wise"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            View Lot Report →
          </Link>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto">
          {list.map((lot, idx) => (
            <div key={idx} className="p-3 text-[11px] space-y-2 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-900 dark:text-white text-xs">
                  {lot.lotNo}
                </span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  Yard: {lot.yardNo}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-2 dark:bg-slate-800/50">
                <div>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 block">Original</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {Number(lot.originalWeightKg || 0).toLocaleString("en-IN")} kg
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 block">Processed</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {Number(lot.processedWeightKg || 0).toLocaleString("en-IN")} kg
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 block">Remaining</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">
                    {Number(lot.remainingWeightKg || 0).toLocaleString("en-IN")} kg
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500 dark:text-slate-400">
                {lot.vendors && lot.vendors.length > 0 && (
                  <span>🏢 <b>Vendors:</b> {lot.vendors.slice(0, 2).join(", ")}</span>
                )}
                {lot.users && lot.users.length > 0 && (
                  <span>👷 <b>Users:</b> {lot.users.slice(0, 2).join(", ")}</span>
                )}
                {lot.totalSalesAmountRs > 0 && (
                  <span>💰 <b>Sales:</b> ₹{Number(lot.totalSalesAmountRs).toLocaleString("en-IN")}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── P&L Statement Summary Card ──────────────────────────────────────────────
  if (type === "pnl_summary" && data) {
    const rev = Number(data.totalRevenue || data.totalSalesGrandTotal || 0);
    const exp = Number(data.totalExpenses || 0);
    const profit = Number(data.grossProfit !== undefined ? data.grossProfit : (rev - exp));
    const isProfit = profit >= 0;

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📊 Profit & Loss (P&L) Summary
          </span>
          <Link
            to="/reports/pnl-report"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            View Full P&L →
          </Link>
        </div>
        <div className="p-3 text-[11px] space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-emerald-50/60 p-2 dark:bg-emerald-950/20">
              <span className="text-[10px] text-emerald-700 dark:text-emerald-400 block font-medium">Total Revenue</span>
              <span className="text-xs font-bold text-emerald-900 dark:text-emerald-200">
                ₹{rev.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="rounded-lg bg-rose-50/60 p-2 dark:bg-rose-950/20">
              <span className="text-[10px] text-rose-700 dark:text-rose-400 block font-medium">Total Expenses</span>
              <span className="text-xs font-bold text-rose-900 dark:text-rose-200">
                ₹{exp.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-500 dark:text-slate-400 rounded-lg bg-slate-50 p-2 dark:bg-slate-800/40">
            <div>Purchases: <b className="text-slate-800 dark:text-slate-200">₹{Number(data.totalPurchaseAmountInRS || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</b></div>
            <div>Overheads: <b className="text-slate-800 dark:text-slate-200">₹{Number(data.totalOverheadExpenseAmount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</b></div>
            <div>Labour: <b className="text-slate-800 dark:text-slate-200">₹{Number(data.totalLabourCost || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</b></div>
          </div>
          <div className={`flex items-center justify-between rounded-lg p-2 font-medium ${isProfit ? "bg-emerald-100/50 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-rose-100/50 text-rose-900 dark:bg-rose-900/30 dark:text-rose-300"}`}>
            <span>Net {isProfit ? "Profit" : "Loss"}:</span>
            <span className="text-xs font-bold">
              ₹{Math.abs(profit).toLocaleString("en-IN", { maximumFractionDigits: 0 })} ({Number(data.grossProfitPercentage || data.profitMarginPercentage || 0).toFixed(1)}%)
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ── Customer Ledger Card ────────────────────────────────────────────────────
  if (type === "customer_ledger" && data.customerInfo) {
    const cust = data.customerInfo;
    const summary = data.summary || {};
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[200px]">
            👤 {cust.customerName}
          </span>
          <Link
            to="/reports/customer-report"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Customer Ledger →
          </Link>
        </div>
        <div className="p-3 text-[11px] space-y-2">
          <div className="grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-2 dark:bg-slate-800/50 text-center">
            <div>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 block">Total Invoiced</span>
              <span className="font-bold text-slate-900 dark:text-white">
                ₹{Number(summary.totalInvoiceAmount || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 block">Total Paid</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                ₹{Number(summary.totalPaid || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 block">Outstanding</span>
              <span className="font-bold text-rose-600 dark:text-rose-400">
                ₹{Number(summary.totalRemaining || 0).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 flex justify-between">
            <span>Invoices: <b>{summary.totalInvoices || data.invoicesCount || 0}</b></span>
            <span>Mobile: <b>{cust.mobileNo || "N/A"}</b></span>
            <span>GST: <b>{cust.companyGSTNo || "N/A"}</b></span>
          </div>
        </div>
      </div>
    );
  }

  if (type === "lot_stock_summary" && data.lots) {
    const list = data.lots.slice(0, 8);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📦 Lot Stock Balances ({data.totalLots || list.length} Lots)
          </span>
          <Link
            to="/lot"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            View Lots →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">
              <tr>
                <th className="px-3 py-1.5 font-medium">Lot No</th>
                <th className="px-2 py-1.5 font-medium text-right">Available (kg)</th>
                <th className="px-3 py-1.5 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.map((lot, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                  <td className="px-3 py-1.5 font-medium text-slate-900 dark:text-slate-100 truncate max-w-[130px]">
                    {lot.lotNo}
                    {lot.metalGrades ? (
                      <span className="block text-[9px] text-slate-400 font-normal truncate max-w-[120px]">
                        {lot.metalGrades}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800 dark:text-slate-200">
                    {Number(lot.remainingStockKg || 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <span
                      className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                        Number(lot.remainingStockKg || 0) <= 0
                          ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          : Number(lot.remainingStockKg || 0) < 1000
                          ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                          : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                      }`}
                    >
                      {Number(lot.remainingStockKg || 0) <= 0
                        ? "Depleted"
                        : Number(lot.remainingStockKg || 0) < 1000
                        ? "Low Stock"
                        : "Optimal"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.totalRemainingKg !== undefined && (
          <div className="border-t border-slate-100 bg-slate-50/50 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-300">
            Total Lot Inventory:{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {Number(data.totalRemainingKg).toLocaleString("en-IN", {
                maximumFractionDigits: 1,
              })}{" "}
              kg
            </span>
          </div>
        )}
      </div>
    );
  }

  if (type === "shipments" && data.shipments) {
    const list = data.shipments.slice(0, 5);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            🚢 Maritime Shipments & Demurrage
          </span>
          <Link
            to="/bl-master"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            BL Tracker →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((s, idx) => (
            <div key={idx} className="p-2.5 text-[11px] hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-900 dark:text-white">{s.blNumber}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {s.status}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-slate-500 dark:text-slate-400">
                <span>Shipping: {s.shippingLine || "N/A"}</span>
                <span>Port ETA: {s.portEta || "N/A"}</span>
              </div>
              {s.demurrageRisk === "HIGH RISK - EXPIRING SOON" ? (
                <div className="mt-1.5 flex items-center gap-1 rounded bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                  <span>⚠️ Demurrage Alert: {s.daysLeftForIcd} days left for ICD</span>
                </div>
              ) : (
                <div className="mt-1 text-[10px] text-slate-500">
                  ICD ETA: {s.icdEta || "N/A"} ({s.daysLeftForIcd} days left)
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "ap_aging" && data.payables) {
    const list = data.payables.slice(0, 5);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            💳 Accounts Payable (AP Aging)
          </span>
          <Link
            to="/reports/ap-aging"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Full Aging →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((p, idx) => (
            <div key={idx} className="p-2.5 text-[11px] hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between font-semibold text-slate-900 dark:text-white">
                <span>{p.vendorName}</span>
                <span className="text-rose-600 dark:text-rose-400">
                  ₹{Number(p.outstandingBalance || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="mt-0.5 flex justify-between text-[10px] text-slate-500">
                <span>Bill #{p.billNo}</span>
                <span>Due: {p.dueDate || "N/A"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "ar_aging" && data.receivables) {
    const list = data.receivables.slice(0, 5);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            💰 Accounts Receivable (AR Aging)
          </span>
          <Link
            to="/reports/ar-aging"
            onClick={() => onNavigate && onNavigate()}
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Full Aging →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((r, idx) => (
            <div key={idx} className="p-2.5 text-[11px] hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between font-semibold text-slate-900 dark:text-white">
                <span>{r.customerName}</span>
                <span className="text-emerald-600 dark:text-emerald-400">
                  ₹{Number(r.outstandingBalance || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="mt-0.5 flex justify-between text-[10px] text-slate-500">
                <span>Invoice #{r.invoiceNo}</span>
                <span>Due: {r.dueDate || "N/A"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "directory_search" && data.results && data.results.length > 0) {
    const list = data.results.slice(0, 4);
    const getEntityConfig = (entityType) => {
      const typeKey = String(entityType || '').toLowerCase().replace(/[^a-z]/g, '');
      switch (typeKey) {
        case "customer":
          return {
            icon: "👤",
            badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800",
            route: "/customer-master",
            label: "Customer Profile"
          };
        case "vendor":
        case "party":
          return {
            icon: "🏢",
            badge: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800",
            route: "/settings-master/party",
            label: "Vendor Directory"
          };
        case "yard":
          return {
            icon: "📍",
            badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800",
            route: "/yard",
            label: "Yard Master"
          };
        case "lot":
          return {
            icon: "📦",
            badge: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800",
            route: "/settings-master/lot",
            label: "Lot Inventory"
          };
        case "bl":
        case "billoflading":
          return {
            icon: "🚢",
            badge: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 border-teal-200 dark:border-teal-800",
            route: "/bl-master",
            label: "BL Tracker"
          };
        case "stockcategory":
          return {
            icon: "🏷️",
            badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
            route: "/stock-category",
            label: "Stock Category"
          };
        case "shippingline":
          return {
            icon: "⚓",
            badge: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800",
            route: "/bl-master",
            label: "Shipping Line"
          };
        default:
          return {
            icon: "📋",
            badge: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
            route: "/dashboard",
            label: "Directory"
          };
      }
    };

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              ⚡ Enterprise Match ({data.count || list.length})
            </span>
            {data.query && (
              <span className="max-w-[120px] truncate rounded bg-slate-200/70 px-1.5 py-0.5 text-[9px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                "{data.query}"
              </span>
            )}
          </div>
          <span className="text-[10px] font-medium text-primary-600 dark:text-primary-400">
            Vector Semantic RAG
          </span>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((item, idx) => {
            const config = getEntityConfig(item.type);
            return (
              <div
                key={idx}
                className="p-3 text-[11px] transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${config.badge}`}
                      >
                        <span>{config.icon}</span>
                        <span>{item.type}</span>
                      </span>
                      <h4 className="truncate font-bold text-slate-900 dark:text-white">
                        {item.title}
                      </h4>
                    </div>
                    {item.subtitle && (
                      <p className="mt-1 truncate text-[11px] text-slate-600 dark:text-slate-300">
                        {item.subtitle}
                      </p>
                    )}
                    {item.metadata && Object.keys(item.metadata).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(item.metadata)
                          .filter(([_, v]) => v !== null && v !== undefined && v !== "")
                          .slice(0, 3)
                          .map(([k, v]) => (
                            <span
                              key={k}
                              className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                            >
                              <span className="font-semibold capitalize text-slate-500 dark:text-slate-400 mr-1">
                                {k}:
                              </span>
                              {String(v)}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                  <Link
                    to={config.route}
                    onClick={() => onNavigate && onNavigate()}
                    className="shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-primary-600 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-primary-400 dark:hover:bg-slate-700"
                  >
                    Open →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "user_directory" && data.users) {
    const users = data.users;
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              👥 System Users Directory ({data.totalUsers || users.length} Users)
            </span>
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              {data.activeUsers || 0} Active
            </span>
          </div>
          <Link
            to="/user-master"
            onClick={() => onNavigate && onNavigate()}
            className="text-[10px] font-semibold text-primary-600 hover:underline dark:text-primary-400"
          >
            Manage Users →
          </Link>
        </div>

        {data.rolesBreakdown && Object.keys(data.rolesBreakdown).length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-b border-slate-100 bg-slate-50/40 px-3 py-1.5 dark:border-slate-800 dark:bg-slate-800/30">
            {Object.entries(data.rolesBreakdown).map(([role, count]) => (
              <span
                key={role}
                className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                <span className="font-semibold">{role}:</span> {count}
              </span>
            ))}
          </div>
        )}

        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {users.map((u, idx) => {
            const roleColor =
              u.roleName === 'Admin'
                ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800'
                : u.roleName === 'Manager'
                ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800';

            return (
              <div
                key={idx}
                className="flex items-center justify-between p-2.5 text-[11px] hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px]">
                    {(u.fullName || u.username || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-slate-900 dark:text-white truncate">
                        {u.fullName}
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">
                        (@{u.username})
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                      {u.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold ${roleColor}`}>
                    {u.roleName}
                  </span>
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      u.isActive ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                    title={u.status}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "role_permissions" && data.roles) {
    const roles = data.roles;
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            🛡️ System Security Roles & Permissions ({data.totalRoles || roles.length} Roles)
          </span>
          <Link
            to="/role-master"
            onClick={() => onNavigate && onNavigate()}
            className="text-[10px] font-semibold text-primary-600 hover:underline dark:text-primary-400"
          >
            Role Master →
          </Link>
        </div>

        {data.menuAccessList && data.menuAccessList.length > 0 && (
          <div className="border-b border-slate-100 bg-amber-50/50 p-2.5 dark:border-slate-800 dark:bg-amber-950/20">
            <span className="text-[10px] font-bold text-amber-900 dark:text-amber-200">
              Module Access Matrix:
            </span>
            <div className="mt-1 space-y-1">
              {data.menuAccessList.map((m, idx) => (
                <div key={idx} className="flex items-center justify-between text-[10px]">
                  <span className="font-semibold text-slate-800 dark:text-slate-200">{m.menuName}</span>
                  <span className="text-slate-600 dark:text-slate-400">
                    Permitted: <span className="font-bold text-primary-600 dark:text-primary-400">{m.permittedRoles.join(', ')}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 p-2 space-y-2">
          {roles.map((r, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 text-[11px] dark:border-slate-800 dark:bg-slate-800/40"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-slate-900 dark:text-white text-xs">
                    {r.roleName}
                  </span>
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                    {r.userCount} {r.userCount === 1 ? 'user' : 'users'}
                  </span>
                </div>
                <span className="text-[9px] font-medium text-emerald-600 dark:text-emerald-400">
                  {r.menuCount} Accessible Modules
                </span>
              </div>

              {r.accessibleMenus && r.accessibleMenus.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.accessibleMenus.map((m, mIdx) => (
                    <span
                      key={mIdx}
                      className="rounded bg-white border border-slate-200/80 px-1.5 py-0.5 text-[9px] text-slate-600 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300"
                    >
                      {m.menuName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "user_profile" && data.user) {
    const u = data.user;
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            👤 User Profile & Permissions
          </span>
          <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            {u.status}
          </span>
        </div>
        <div className="p-3 text-[11px]">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-bold text-primary-700 dark:bg-primary-950 dark:text-primary-300 text-xs">
              {(u.fullName || u.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-bold text-slate-900 dark:text-white text-xs">{u.fullName}</div>
              <div className="text-[10px] text-slate-500">@{u.username} • {u.email}</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 rounded bg-slate-50 p-2 dark:bg-slate-800/50 text-[10px]">
            <div>
              <span className="text-slate-500">Role: </span>
              <span className="font-bold text-slate-800 dark:text-slate-200">{u.roleName}</span>
            </div>
            <div>
              <span className="text-slate-500">Registered: </span>
              <span className="font-bold text-slate-800 dark:text-slate-200">{u.createdDate}</span>
            </div>
          </div>
          {data.accessibleMenus && data.accessibleMenus.length > 0 && (
            <div className="mt-2.5">
              <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300">
                Accessible Modules ({data.totalAccessibleMenus}):
              </span>
              <div className="mt-1 flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                {data.accessibleMenus.map((m, idx) => (
                  <span
                    key={idx}
                    className="rounded bg-white border border-slate-200 px-1.5 py-0.5 text-[9px] text-slate-600 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-300"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (type === "enterprise_query" && data.rows && data.rows.length > 0) {
    const rows = data.rows.slice(0, 5);
    const columns = Object.keys(rows[0] || {}).slice(0, 4);

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📊 Database Insights ({data.rowCount || rows.length} Records)
          </span>
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            Verified Analytics
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">
              <tr>
                {columns.map((col) => (
                  <th key={col} className="px-3 py-1.5 font-medium capitalize">
                    {col.replace(/([A-Z])/g, " $1")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((r, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                  {columns.map((col) => (
                    <td
                      key={col}
                      className="px-3 py-1.5 font-medium text-slate-900 dark:text-slate-100 truncate max-w-[140px]"
                    >
                      {r[col] !== null && r[col] !== undefined ? String(r[col]) : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
}

